import { Contract } from "@edp-components/edp-contract";
import { PaymentResultAction } from "@edp-store/actions/payment.actions";
import { AgreementEligibilityCode } from "@edp-enums/agreement-eligibility.enum";
import CryptoJS from "crypto-js";
import moment from "moment";

export interface IPaymentServiceArgs {
    contractId: string;
    accountNumber: string;
    initialDate: string;
    endDate: string;
    paymentRequestDate: string;
    phone?: string;
    bank?: string;
    amount: number;
    type: string;
    captchaToken?: string;
    mbwayDataId?: string;
}

export enum PaymentStatus {
    RECAPTCHA_GET = "RECAPTCHA_GET",
    RECAPTCHA_FAILED = "RECAPTCHA_FAILED",
    RECAPTCHA_RECEIVED = "RECAPTCHA_RECEIVED",
    PAYMENT_CREATE = "PAYMENT_CREATE",
    PAYMENT_CREATED = "PAYMENT_CREATED",
    PAYMENT_FAILED = "PAYMENT_FAILED",
    PAYMENT_STATUS_PENDING = "PAYMENT_STATUS_PENDING",
    PAYMENT_STATUS_SUCCESS = "PAYMENT_STATUS_SUCCESS",
    PAYMENT_STATUS_REJECTED = "PAYMENT_STATUS_REJECTED",
    PAYMENT_STATUS_CANCELLED = "PAYMENT_STATUS_CANCELLED",
    PAYMENT_STATUS_EXPIRED = "PAYMENT_STATUS_EXPIRED",
}

export enum PaymentServiceStatus {
    PENDING = "PENDING",
    SUCCESS = "SUCCESS",
    REJECTED = "REJECTED",
    CANCELLED = "CANCELLED",
    EXPIRED = "EXPIRED",
}

export interface IPaymentStatusService {
    status: PaymentServiceStatus;
}

export abstract class PaymentResult extends PaymentResultAction {
    public id: string = null;
    public initialDate: string = null;
    public endDate: string = null;
    public requestDate: moment.Moment = null;
    public phone: string = null;
    public bankName: string = null;
    public bankIdentifier: string = null;
    public captchaToken: string = null;
    public debt: boolean = null;
    public status: PaymentStatus = null;
    public error: Error = null;
    public redirectUrl: string = null;
    public paymentType: string = null;

    constructor(public contract: Contract, public amount: number, public isDebt = false) {
        super();
        this.initialDate = moment()
            .subtract(365, "days")
            .format("YYYY-MM-DD");
        this.endDate = moment().format("YYYY-MM-DD");
        this.debt = isDebt;
        this.paymentType = this instanceof OpenBankingPayment ? "openbanking" : "mbway";
        const entity = this.phone || this.bankName;
        this.id = CryptoJS.SHA256(this.paymentType + "-" + entity + "-" + this.amount + "-" + this.contract.ContractId).toString(
            CryptoJS.enc.Hex
        );
    }

    public getRequestArgs(): IPaymentServiceArgs {
        return {
            contractId: this.contract.ContractId,
            accountNumber: this.contract.AccountNumber,
            initialDate: this.initialDate,
            endDate: this.endDate,
            paymentRequestDate: this.requestDate && this.requestDate.format ? this.requestDate.format("YYYY-MM-DD HH:mm:ss") : null,
            amount: this.amount,
            type: this.debt ? "debt" : "notDebt",
            captchaToken: this.captchaToken,
            mbwayDataId: this.id,
        };
    }

    public isPending() {
        return (this.status === PaymentStatus.PAYMENT_STATUS_PENDING || this.status === PaymentStatus.PAYMENT_CREATED) && this.requestDate;
    }

    public setStatus(status: PaymentStatus, err?: Error) {
        this.status = status;
        if (err) {
            this.error = err;
        }
        return this;
    }

    public abstract getCopy(): PaymentResult;

    protected _getCopy(target: PaymentResult) {
        Object.assign(target, this);
        return target;
    }
}

export class MbWayPayment extends PaymentResult {
    constructor(phone: string, contract: Contract, amount: number, isDebt = false) {
        super(contract, amount, isDebt);
        this.phone = phone;
    }

    public getRequestArgs(): IPaymentServiceArgs {
        const r = super.getRequestArgs.call(this);
        r.phone = this.phone;

        return r;
    }

    public getCopy(): PaymentResult {
        const t = new MbWayPayment(this.phone, this.contract, this.amount, this.debt);
        return this._getCopy(t);
    }
}

export class OpenBankingPayment extends PaymentResult {
    constructor(bankName: string, bankIdentifier: string, contract: Contract, amount: number, isDebt = false) {
        super(contract, amount, isDebt);
        this.bankName = bankName;
        this.bankIdentifier = bankIdentifier;
    }

    public getRequestArgs(): IPaymentServiceArgs {
        const r = super.getRequestArgs.call(this);
        r.bank = this.bankIdentifier;

        return r;
    }

    public getCopy(): PaymentResult {
        const t = new OpenBankingPayment(this.bankName, this.bankIdentifier, this.contract, this.amount, this.debt);
        return this._getCopy(t);
    }
}

export interface AgreementContact {
    recaptcha: string;
    nif: string;
}

export interface AgreementContactResponse {
    contact?: AgreementContactResponseContact;
    used?: AgreementContactUSed;
    nif?: string;
    transactionId?: string;
}

export enum AgreementContactUSed {
    NOT_FOUND_CUSTOMER = "not_found_customer",
    NOT_FOUND_CONTACT = "not_found_contact",
    EMAIL = "email",
    MOBILE = "mobile",
}

/* SHITTY NAME :( */
export interface AgreementContactResponseContact {
    email?: string;
    mobile?: string;
}

export interface AgreementAgreementSend {
    nif: string;
}

export interface AgreementValidated {
    nif: string;
    code: string;
}

export interface AgreementValidatedResponse {
    status: boolean;
    message: string;
    transactionId: string;
}

export interface AgreementEligibility {
    nif: string;
    recaptcha: string;
}

export interface AgreementEligibilityResponse {
    status: boolean;
    accountNumber?: string;
    code?: AgreementEligibilityCode;
    debits: AgreementEligibilityDebit[];
    data?: AgreementValidatedResponseData;
    message?: string;
    error?: string;
}

export enum AgreementEligibilityError {
    EF006 = "EF006",
    E001 = "E001",
    E002 = "E002",
    E003 = "E003",
    E004 = "E004",
    E005 = "E005",
    E006 = "E006",
    E007 = "E007",
    E008 = "E008",
    E009 = "E009",
    E010 = "E010",
    E011 = "E011",
    E012 = "E012",
    E013 = "E013",
}

export interface AgreementValidatedResponseData {
    accountNumber: string;
    error?: string;
}

export interface AgreementEligibilityDebit {
    accountNumber: string;
    name: string;
    adress: AgreementEligibilityDebitAddress;
    value: string;
    pp: string;
    ppDocNumber: string;
    months: AgreementEligibilityDebitMonth[];
}

export interface AgreementEligibilityDebitAddress {
    city: string;
    zone: string;
    county: string;
    number: string;
    parish: string;
    street: string;
    country: string;
    district: string;
    location: string;
    postalCode: string;
    houseNumber: string;
}

export interface AgreementEligibilityDebitMonth {
    month: string;
    amount: string;
}

export interface AgreementGenerate {
    nif: string;
    recaptcha: string;
    account: string;
}

export interface AgreementCheckout {
    nif: string;
    account: string;
}

export interface AgreementHistory {
    nif?: string;
    account?: string;
    operation: string;
    description?: string;
    content?: any;
    contact?: string;
    user_id?: number;
    user?: string;
}
export enum AgreementHistoryUserStatus {
    PUBLIC = "PUBLIC",
    PRIVATE = "PRIVATE",
}
